<template>
  <div class="dashboard-container">
    <div style="padding-top: 55px" />
    <div class="container-fluid">
      <!-- Header -->
      <div class="row mb-3">
        <div class="col-12">
          <h2 class="mb-0"><i class="fa fa-dashboard"></i> 사용량 대시보드</h2>
          <p class="text-muted">분석일 기준 건수 통계</p>
        </div>
      </div>

      <!-- Loading / Error -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
        <p class="mt-2">데이터를 불러오는 중...</p>
      </div>
      <div v-if="error" class="alert alert-danger" role="alert">
        <i class="fa fa-exclamation-triangle"></i> {{ error }}
        <button @click="loadDashboardData" class="btn btn-sm btn-outline-danger ml-2">다시 시도</button>
      </div>

      <div v-if="!loading && !error && dashboardData">
        <!-- 한도/쿼터 (Mongo) — 데모/제한 계정 -->
        <div class="row mb-4" v-if="showQuotaCard">
          <div class="col-12">
            <div class="card">
              <div class="card-header"><h5 class="mb-0"><i class="fa fa-id-card"></i> 한도 / 쿼터</h5></div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4">
                    <div class="text-center">
                      <h3 class="text-secondary">
                        <span v-if="quotaLimit === 'Unlimited'">-</span>
                        <span v-else>{{ quotaLimit }}</span>
                      </h3>
                      <p class="text-muted mb-0">AI 분석 한도</p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <h3 class="text-success">
                        <span v-if="quotaRemaining === 'Unlimited'">-</span>
                        <span v-else>{{ quotaRemaining }}</span>
                      </h3>
                      <p class="text-muted mb-0">남은 AI 한도</p>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="text-center">
                      <h3>
                        <span v-if="accountType === 'unlimited'" class="badge badge-success">Unlimited</span>
                        <span v-else class="badge badge-warning text-dark">Demo</span>
                      </h3>
                      <p class="text-muted mb-0">계정 타입</p>
                    </div>
                  </div>
                </div>
                <p class="text-muted small mb-0 mt-2">
                  * 한도/잔여는 MongoDB(쿼터 집행) 기준이며, 아래 분석 건수(telemetry · 분석일 기준)와 의미가 다릅니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- API Demo Quota (Demo API Users) -->
        <div class="row mb-4" v-if="isDemoApiUser">
          <div class="col-12">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0"><i class="fa fa-building"></i> API Demo Quota</h5>
                <div class="d-flex align-items-center">
                  <div class="btn-group btn-group-sm mr-2">
                    <button type="button" :class="['btn', quotaShowAll ? 'btn-outline-secondary' : 'btn-primary']"
                      @click="quotaShowAll = false; quotaPage = 1">Active만</button>
                    <button type="button" :class="['btn', quotaShowAll ? 'btn-primary' : 'btn-outline-secondary']"
                      @click="quotaShowAll = true; quotaPage = 1">전체</button>
                  </div>
                  <button @click="loadInstitutionQuotas" class="btn btn-sm btn-outline-primary"><i class="fa fa-refresh"></i></button>
                </div>
              </div>
              <div class="card-body">
                <div v-if="institutionQuotasLoading" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <span class="ml-2">로딩 중...</span>
                </div>
                <div v-else-if="filteredQuotas.length === 0" class="text-center text-muted py-3">
                  <span v-if="quotaShowAll">아직 등록된 기관이 없습니다. API 요청 시 X-Institution 헤더로 자동 생성됩니다.</span>
                  <span v-else>Active 상태의 기관이 없습니다. <a href="#" @click.prevent="quotaShowAll = true">전체 보기</a></span>
                </div>
                <template v-else>
                  <div class="table-responsive">
                    <table class="table table-striped table-hover mb-0">
                      <thead class="thead-dark">
                        <tr><th>기관</th><th>상태</th><th>사용량</th><th>남은 한도</th><th>만료일</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="quota in pagedQuotas" :key="quota.institution">
                          <td><strong>{{ quota.institution }}</strong></td>
                          <td>
                            <span v-if="quota.status === 'active'" class="badge badge-success">Active</span>
                            <span v-else-if="quota.status === 'expired'" class="badge badge-secondary">Expired</span>
                            <span v-else-if="quota.status === 'exhausted'" class="badge badge-danger">Exhausted</span>
                            <span v-else class="badge badge-dark">{{ quota.status }}</span>
                          </td>
                          <td>{{ quota.used }} / {{ quota.limit }}</td>
                          <td><span :class="quota.remaining <= 10 ? 'text-danger font-weight-bold' : 'text-success'">{{ quota.remaining }}</span></td>
                          <td>{{ formatQuotaDate(quota.expires_at) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <Pagination v-if="quotaTotalPages > 1" :pages="quotaTotalPages" @pagechange="onQuotaPageChange" />
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- 역할별 통계 탭 -->
        <div v-if="availableTabs.length > 0">
          <ul class="nav nav-tabs mb-3">
            <li class="nav-item" v-for="tab in availableTabs" :key="tab.key">
              <a class="nav-link" :class="{ active: activeTab === tab.key }" href="#"
                 @click.prevent="selectTab(tab.key)">{{ tab.label }}</a>
            </li>
          </ul>

          <!-- Admin scope 선택 (요약 제외 탭) -->
          <div class="row mb-2" v-if="isAdmin && activeTab !== 'summary'">
            <div class="col-md-6">
              <label class="small mb-1">{{ scopeLabel }} 선택 (관리자)</label>
              <select v-model="adminScopeValue" class="form-control form-control-sm"
                      :disabled="scopeOptionsLoading" @change="loadStats">
                <option v-if="scopeOptionsLoading" value="">목록 불러오는 중…</option>
                <option v-else-if="scopeOptions.length === 0" value="">해당 기간 데이터 있는 {{ scopeLabel }} 없음</option>
                <option v-for="opt in scopeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
          </div>

          <!-- 기간 + granularity -->
          <div class="row mb-3">
            <div class="col-md-5">
              <label class="small mb-1">조회 기간</label>
              <flat-pickr v-model="dateRange" :config="dateConfig" class="form-control"
                          placeholder="날짜 범위를 선택하세요" @on-change="onDateChange" />
            </div>
            <div class="col-md-7 d-flex align-items-end flex-wrap">
              <button type="button" class="btn btn-outline-secondary btn-sm mr-1 mb-1" @click="setRangeMonth(1)">이번 달</button>
              <button type="button" class="btn btn-outline-secondary btn-sm mr-1 mb-1" @click="setRangeMonth(3)">3개월</button>
              <button type="button" class="btn btn-outline-secondary btn-sm mr-1 mb-1" @click="setRangeMonth(6)">6개월</button>
              <button type="button" class="btn btn-outline-secondary btn-sm mr-1 mb-1" @click="setRangeMonth(12)">12개월</button>
              <span class="mx-2"></span>
              <div class="btn-group btn-group-sm mb-1 mr-2">
                <button type="button" :class="['btn', granularity === 'daily' ? 'btn-primary' : 'btn-outline-secondary']"
                        @click="setGranularity('daily')">일별</button>
                <button type="button" :class="['btn', granularity === 'monthly' ? 'btn-primary' : 'btn-outline-secondary']"
                        @click="setGranularity('monthly')">월별</button>
              </div>
              <button @click="loadStats" class="btn btn-outline-primary btn-sm mb-1"><i class="fa fa-refresh"></i> 조회</button>
            </div>
          </div>
          <div class="row mb-2" v-if="rangeWarning">
            <div class="col-12"><div class="alert alert-warning alert-sm py-2 mb-0">
              <i class="fa fa-exclamation-triangle"></i> {{ rangeWarning }}
            </div></div>
          </div>

          <!-- Stats loading / error -->
          <div v-if="statsLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
            <p class="mt-2">데이터를 불러오는 중...</p>
          </div>
          <div v-else-if="statsError" class="alert alert-warning" role="alert">
            <i class="fa fa-exclamation-triangle"></i> {{ statsError }}
          </div>

          <div v-else-if="statsData">
            <!-- Summary cards -->
            <div class="row mb-4">
              <div class="col-md-3">
                <div class="card"><div class="card-body text-center">
                  <h3 class="text-primary">{{ statsData.summary.total_analyses }}</h3>
                  <p class="text-muted mb-0">전체 분석</p>
                </div></div>
              </div>
              <div class="col-md-3">
                <div class="card"><div class="card-body text-center">
                  <h3 class="text-info">{{ statsData.summary.total_screening }}</h3>
                  <p class="text-muted mb-0">검진</p>
                </div></div>
              </div>
              <div class="col-md-3">
                <div class="card"><div class="card-body text-center">
                  <h3 class="text-warning">{{ statsData.summary.total_non_reimbursed }}</h3>
                  <p class="text-muted mb-0">비급여</p>
                </div></div>
              </div>
              <div class="col-md-3">
                <div class="card"><div class="card-body text-center">
                  <h3 class="text-success">{{ statsData.summary.institution_count }}</h3>
                  <p class="text-muted mb-0">기관 수</p>
                </div></div>
              </div>
            </div>

            <!-- 전체요약: 데모 계정 분리 집계 (참고 · billing 제외) -->
            <div v-if="activeTab === 'summary' && statsData.summary_demo" class="row mb-4">
              <div class="col-12">
                <div class="card border-secondary">
                  <div class="card-header bg-light py-2">
                    <h6 class="mb-0 text-muted"><i class="fa fa-flask"></i> 데모 계정 (참고 · billing 제외)</h6>
                  </div>
                  <div class="card-body py-3">
                    <div class="row text-center">
                      <div class="col"><h4 class="text-secondary mb-0">{{ statsData.summary_demo.total_analyses }}</h4><small class="text-muted">데모 전체 분석</small></div>
                      <div class="col"><h4 class="text-secondary mb-0">{{ statsData.summary_demo.total_screening }}</h4><small class="text-muted">데모 검진</small></div>
                      <div class="col"><h4 class="text-secondary mb-0">{{ statsData.summary_demo.total_non_reimbursed }}</h4><small class="text-muted">데모 비급여</small></div>
                      <div class="col"><h4 class="text-secondary mb-0">{{ statsData.summary_demo.institution_count }}</h4><small class="text-muted">데모 기관 수</small></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Institutions table -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header"><h5 class="mb-0"><i class="fa fa-hospital-o"></i> 기관별 사용량</h5></div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-striped table-hover">
                        <thead class="thead-dark">
                          <tr><th>기관명</th><th>전체 분석</th><th>검진</th><th>비급여</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="inst in statsData.institutions" :key="inst.name">
                            <td>
                              <a href="#" @click.prevent="openInstitutionDetail(inst)" class="text-primary">
                                <strong>{{ inst.name }}</strong>
                              </a>
                            </td>
                            <td>{{ inst.total_analyses }}</td>
                            <td><span class="badge badge-primary">{{ inst.total_screening }}</span></td>
                            <td><span class="badge badge-warning text-dark">{{ inst.total_non_reimbursed }}</span></td>
                          </tr>
                          <tr v-if="statsData.institutions.length === 0">
                            <td colspan="4" class="text-center text-muted">데이터가 없습니다</td>
                          </tr>
                        </tbody>
                        <tfoot class="table-dark">
                          <tr>
                            <th>합계</th>
                            <th>{{ statsData.summary.total_analyses }}</th>
                            <th>{{ statsData.summary.total_screening }}</th>
                            <th>{{ statsData.summary.total_non_reimbursed }}</th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Institution Detail Modal (일/월별 상세) -->
            <b-modal v-model="showInstitutionModal" size="lg" hide-footer
              :title="selectedInstitution ? selectedInstitution.name + ' - ' + (granularity === 'daily' ? '일별' : '월별') + ' 상세' : ''"
              @hidden="closeInstitutionModal">
              <div v-if="selectedInstitution" class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead>
                    <tr><th>{{ granularity === 'daily' ? '일자' : '월' }}</th><th>전체 분석</th><th>검진</th><th>비급여</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(mu, idx) in selectedInstitution.usage_data" :key="idx">
                      <td>{{ formatBucket(mu) }}</td>
                      <td>{{ mu.ai_analysis_count }}</td>
                      <td><span class="badge badge-primary">{{ mu.ai_analysis_screening }}</span></td>
                      <td><span class="badge badge-warning text-dark">{{ mu.ai_analysis_non_reimbursed }}</span></td>
                    </tr>
                    <tr v-if="selectedInstitution.usage_data.length === 0">
                      <td colspan="4" class="text-center text-muted">데이터 없음</td>
                    </tr>
                  </tbody>
                  <tfoot class="table-dark">
                    <tr>
                      <th>합계</th>
                      <th>{{ selectedInstitution.total_analyses }}</th>
                      <th>{{ selectedInstitution.total_screening }}</th>
                      <th>{{ selectedInstitution.total_non_reimbursed }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </b-modal>
          </div>
        </div>

        <div v-else class="alert alert-info">표시할 통계 페이지 권한이 없습니다.</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import flatPickr from 'vue-flatpickr-component'
import Pagination from '@/components/Pagination'
import 'flatpickr/dist/flatpickr.css'
import { Korean } from 'flatpickr/dist/l10n/ko.js'

const TAB_ENDPOINTS = {
  summary: '/api/dashboard/stats/summary',
  distributor: '/api/dashboard/stats/distributor',
  hq: '/api/dashboard/stats/hq',
  institution: '/api/dashboard/stats/institution'
}

export default {
  name: 'Dashboard',
  components: { flatPickr, Pagination },
  data() {
    return {
      loading: true,
      error: null,
      dashboardData: null,
      isAdmin: false,

      // tabs / stats
      activeTab: null,
      statsData: null,
      statsLoading: false,
      statsError: null,
      dateRange: [],
      granularity: 'daily',
      adminScopeValue: '',
      scopeOptions: [],
      scopeOptionsLoading: false,
      // 조회 실패와 "조건에 맞는 대상 없음" 을 구분한다 (#410 P2): 실패를 빈 목록으로
      // 보여주면 Telemetry 오류가 "데이터 없음" 으로 읽힌다.
      scopeOptionsError: null,
      scopeReqSeq: 0,
      rangeWarning: '',
      dateConfig: { mode: 'range', dateFormat: 'Y-m-d', locale: Korean },
      // M2(PR #333): live read 요청 시퀀싱/디바운스 — 최신 요청만 커밋, 연속 변경 1회로 합침
      statsReqSeq: 0,
      statsDebounceTimer: null,

      // institution detail
      selectedInstitution: null,
      showInstitutionModal: false,

      // demo quota
      institutionQuotas: [],
      institutionQuotasLoading: false,
      quotaShowAll: false,
      quotaPage: 1,
      quotaPerPage: 20
    }
  },
  computed: {
    isDistributor() { return this.$store.getters['user/isDistributor'] },
    isHq() {
      var ui = this.dashboardData && this.dashboardData.user_info
      return !!(ui && ui.is_hq)
    },
    isApiOnlyUser() { return this.$store.getters['user/isApiOnlyUser'] },
    accountType() {
      return (this.dashboardData && this.dashboardData.current_usage &&
              this.dashboardData.current_usage.account_type) || ''
    },
    quotaLimit() {
      return (this.dashboardData && this.dashboardData.current_usage &&
              this.dashboardData.current_usage.ai_analysis_limit)
    },
    quotaRemaining() {
      return (this.dashboardData && this.dashboardData.current_usage &&
              this.dashboardData.current_usage.ai_analysis_remaining)
    },
    showQuotaCard() {
      // 한도가 의미있는 계정(데모/제한)만. admin/unlimited 는 숨김.
      return !this.isAdmin && !this.isDistributor && this.accountType === 'demo'
    },
    isDemoApiUser() {
      if (!this.dashboardData) return false
      var isDemo = this.accountType === 'demo'
      var canApi = this.$store.getters['user/canAccessApi']
      return isDemo && canApi
    },
    availableTabs() {
      var tabs = []
      if (this.isAdmin) {
        tabs.push({ key: 'summary', label: '전체 요약' })
        tabs.push({ key: 'distributor', label: '유통사' })
        tabs.push({ key: 'hq', label: '기관HQ' })
        tabs.push({ key: 'institution', label: '개별 기관' })
        return tabs
      }
      if (this.isDistributor) tabs.push({ key: 'distributor', label: '유통사' })
      if (this.isHq) tabs.push({ key: 'hq', label: '기관HQ' })
      if (!this.isDistributor && !this.isHq) tabs.push({ key: 'institution', label: '내 기관' })
      return tabs
    },
    scopeLabel() {
      if (this.activeTab === 'distributor') return '유통사'
      if (this.activeTab === 'hq') return '기관HQ'
      if (this.activeTab === 'institution') return '기관'
      return ''
    },
    filteredQuotas() {
      if (this.quotaShowAll) return this.institutionQuotas
      return this.institutionQuotas.filter(function(q) { return q.status === 'active' })
    },
    quotaTotalPages() { return Math.max(1, Math.ceil(this.filteredQuotas.length / this.quotaPerPage)) },
    pagedQuotas() {
      var start = (this.quotaPage - 1) * this.quotaPerPage
      return this.filteredQuotas.slice(start, start + this.quotaPerPage)
    }
  },
  mounted() {
    this.loadDashboardData()
  },
  methods: {
    async loadDashboardData() {
      this.loading = true
      this.error = null
      try {
        var response = await axios.get('/api/dashboard/')
        if (response.data.success) {
          this.dashboardData = response.data.data
          var ui = this.dashboardData.user_info || {}
          this.isAdmin = !!((ui.permissions && ui.permissions.can_view_dashboard_admin) || ui.is_admin)

          if (this.isDemoApiUser) this.loadInstitutionQuotas()

          // 기본 탭 선택 + 통계 로드
          var tabs = this.availableTabs
          if (tabs.length > 0) {
            this.activeTab = tabs[0].key
            this.setRangeMonth(1)  // 이번 달 + loadStats
          }
        } else {
          this.error = '대시보드 데이터를 불러올 수 없습니다.'
        }
      } catch (e) {
        console.error('Dashboard load error:', e)
        this.error = '서버 연결에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },

    selectTab(key) {
      if (this.activeTab === key) return
      this.activeTab = key
      this.adminScopeValue = ''
      this.scopeOptions = []
      this.statsData = null
      this.statsError = null
      this.reloadAll()
    },

    // admin 의 distributor/hq/institution 탭: 옵션(telemetry distinct) 먼저 갱신 후 stats.
    // 그 외(전체요약·비admin)는 바로 stats.
    async reloadAll() {
      if (this.isAdmin && this.activeTab && this.activeTab !== 'summary') {
        await this.loadScopeOptions()
      }
      this.loadStats()
    },

    async loadScopeOptions() {
      // M-1/M-2(PR #338 review): tab 기준 가드는 같은 탭에서 기간만 연속 변경 시 두 요청이
      // 모두 통과해 stale 옵션이 최신을 덮어쓴다. loadStats 의 statsReqSeq 패턴을 복제해
      // 최신 요청(seq === scopeReqSeq)만 옵션/로딩/자동선택을 커밋한다.
      var seq = ++this.scopeReqSeq
      var range = this._resolveRange()
      var params = { type: this.activeTab }
      if (range[0] && range[1]) { params.start_date = range[0]; params.end_date = range[1] }
      this.scopeOptionsLoading = true
      try {
        var resp = await axios.get('/api/dashboard/scope-options', { params: params })
        if (seq !== this.scopeReqSeq) return  // stale 응답 폐기
        var ok = !!(resp.data && resp.data.success)
        this.scopeOptions = (ok && resp.data.options) ? resp.data.options : []
        this.scopeOptionsError = ok
          ? null
          : ((resp.data && resp.data.message) || '조회 대상 목록을 불러올 수 없습니다.')
      } catch (e) {
        if (seq !== this.scopeReqSeq) return  // stale 응답 폐기
        console.error('Scope options load error:', e)
        this.scopeOptions = []
        this.scopeOptionsError = '조회 대상 목록을 불러올 수 없습니다.'
      } finally {
        if (seq === this.scopeReqSeq) this.scopeOptionsLoading = false
      }
      // 최신 요청만 자동선택 커밋 (M-2: seq 가드 안)
      if (seq !== this.scopeReqSeq) return
      // 기존 선택 유지, 없으면 첫 옵션 자동 선택(없으면 빈 값)
      var current = this.adminScopeValue
      var exists = this.scopeOptions.some(function (o) { return o.value === current })
      if (!exists) {
        this.adminScopeValue = this.scopeOptions.length ? this.scopeOptions[0].value : ''
      }
    },

    setGranularity(g) {
      if (this.granularity === g) return
      this.granularity = g
      this.loadStats()
    },

    formatLocalDate(date) {
      var y = date.getFullYear()
      var m = String(date.getMonth() + 1).padStart(2, '0')
      var d = String(date.getDate()).padStart(2, '0')
      return y + '-' + m + '-' + d
    },
    setRangeMonth(months) {
      var end = new Date()
      var start = new Date()
      if (months === 1) {
        start = new Date(end.getFullYear(), end.getMonth(), 1)
      } else {
        start.setMonth(start.getMonth() - months + 1)
        start.setDate(1)
      }
      this.dateRange = [this.formatLocalDate(start), this.formatLocalDate(end)]
      this.reloadAll()
    },
    onDateChange(selectedDates) {
      if (selectedDates.length === 2) {
        this.rangeWarning = ''
        this.reloadAll()
      }
    },
    _resolveRange() {
      var dr = this.dateRange
      var startStr = ''
      var endStr = ''
      if (Array.isArray(dr) && dr.length === 2) {
        startStr = typeof dr[0] === 'string' ? dr[0] : this.formatLocalDate(dr[0])
        endStr = typeof dr[1] === 'string' ? dr[1] : this.formatLocalDate(dr[1])
      } else if (typeof dr === 'string') {
        var sep = dr.indexOf(' to ') !== -1 ? ' to ' : ' ~ '
        var parts = dr.split(sep)
        if (parts.length === 2) { startStr = parts[0].trim(); endStr = parts[1].trim() }
        else if (parts.length === 1 && parts[0].trim()) { startStr = parts[0].trim(); endStr = startStr }
      }
      return [startStr, endStr]
    },

    loadStats() {
      // M2(PR #333): 탭/날짜/granularity 연속 변경 시 마지막 1회만 요청(디바운스).
      var self = this
      if (this.statsDebounceTimer) clearTimeout(this.statsDebounceTimer)
      this.statsDebounceTimer = setTimeout(function () { self._doLoadStats() }, 250)
    },

    async _doLoadStats() {
      if (!this.activeTab) return
      var range = this._resolveRange()
      var startStr = range[0]
      var endStr = range[1]
      this.rangeWarning = ''
      if (startStr && endStr && new Date(startStr) > new Date(endStr)) {
        this.rangeWarning = '시작일이 종료일보다 늦습니다.'
        return
      }

      // admin 의 distributor/hq/institution 탭은 스코프 선택 필수.
      // 선택이 없으면(해당 기간 데이터 있는 대상 없음) 서버 호출(403/empty_scope) 대신 안내.
      if (this.isAdmin && this.activeTab !== 'summary' && !this.adminScopeValue) {
        this.statsLoading = false
        this.statsData = null
        // 목록 조회가 실패한 경우는 "데이터 없음" 이 아니라 그 오류를 그대로 보여준다 (#410 P2).
        this.statsError = this.scopeOptionsError ||
          ('조회할 ' + this.scopeLabel + '를 선택하세요 (해당 기간 데이터 없음).')
        return
      }

      var params = { granularity: this.granularity }
      if (startStr && endStr) { params.start_date = startStr; params.end_date = endStr }
      // admin 임의 스코프 (요약 제외)
      if (this.isAdmin && this.activeTab !== 'summary' && this.adminScopeValue) {
        params[this.activeTab] = this.adminScopeValue
      }

      // M2(PR #333): 요청 시퀀싱 — 느린 stale 응답이 현재 탭 상태를 덮어쓰지 않도록
      // 최신 요청(seq === statsReqSeq)만 statsData/statsError/statsLoading 에 커밋한다.
      var seq = ++this.statsReqSeq
      var tab = this.activeTab
      this.statsLoading = true
      this.statsError = null
      try {
        var response = await axios.get(TAB_ENDPOINTS[tab], { params: params })
        if (seq !== this.statsReqSeq) return  // stale 응답 폐기
        if (response.data.success) {
          this.statsData = response.data
        } else {
          this.statsData = null
          this.statsError = response.data.message || '통계를 불러올 수 없습니다.'
        }
      } catch (e) {
        if (seq !== this.statsReqSeq) return  // stale 응답 폐기
        console.error('Stats load error:', e)
        this.statsData = null
        if (e.response && e.response.data && e.response.data.message) {
          this.statsError = e.response.data.message
        } else if (e.response && e.response.status === 403) {
          this.statsError = '이 페이지를 조회할 권한이 없습니다.'
        } else {
          this.statsError = '서버 연결에 실패했습니다.'
        }
      } finally {
        if (seq === this.statsReqSeq) this.statsLoading = false
      }
    },

    openInstitutionDetail(inst) {
      this.selectedInstitution = inst
      this.showInstitutionModal = true
    },
    closeInstitutionModal() {
      this.selectedInstitution = null
      this.showInstitutionModal = false
    },
    formatBucket(mu) {
      var y = mu.year
      var m = mu.month ? String(mu.month).padStart(2, '0') : '01'
      if (mu.day) return y + '-' + m + '-' + String(mu.day).padStart(2, '0')
      return y + '-' + m
    },

    // demo quota
    loadInstitutionQuotas() {
      this.institutionQuotasLoading = true
      axios.get('/api/dashboard/institution-quotas')
        .then(function (response) {
          if (response.data.success) this.institutionQuotas = response.data.quotas || []
        }.bind(this))
        .catch(function (error) { console.error('Failed to load institution quotas:', error) }.bind(this))
        .finally(function () { this.institutionQuotasLoading = false }.bind(this))
    },
    onQuotaPageChange(page) { this.quotaPage = page },
    formatQuotaDate(dateString) {
      if (!dateString) return 'N/A'
      var date = new Date(dateString)
      var isExpired = date < new Date()
      var formatted = date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      return isExpired ? formatted + ' (만료)' : formatted
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 20px 0;
  min-height: 100vh;
  overflow-y: auto;
  background-color: #f8f9fa;
}
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}
.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}
.table th { border-top: none; }
.badge { font-size: 0.875em; }
.spinner-border { width: 3rem; height: 3rem; }
.nav-tabs .nav-link { cursor: pointer; }
</style>
